import React, {useEffect, useMemo, useState} from 'react';
import styles from './AlertGroup.module.css'
import alertStyles from '../Alert/Alert.module.css'
import commonStyles from '../../styles/common.module.css'
import {useDispatch, useSelector} from "react-redux";
import Alert from "../Alert/Alert";
import {sha256} from "js-sha256";
import AlertService from "../../services/AlertService";
import {openModal, setModalError} from "../../store/reducers/modalReducer";
import {HISTORY_DISPLAY, MAIN_DISPLAY, SILENCED_DISPLAY} from "../../store/actions/DISPLAY_ACTIONS";
import {RECHECKABLE_INPUT_PLUGIN} from "../../utils/vars";
import { DEFAULT_SILENCE_DURATION, formatSilenceEnd, parseSilenceDuration } from "../../utils/silenceDuration";

const AlertGroup = ({group, alertHeight}) => {
    const dispatch = useDispatch()
    const isInspectMode = useSelector(state => state.setHeaderMenuItemValue.inspectMode)
    const userEmail = useSelector(state => state.authReducer.user.userEmail)
    const displayMode = useSelector(state => state.setDisplay.display)
    const recheckedAlerts = useSelector(state => state.webSocket.recheckAlerts)

    const [isAlertsBlockOpened, setIsAlertBlockOpened] = useState('false')
    const [isEnabledSilenceWindow, setEnabledSilenceWindow] = useState(false)
    const [isEnabledSilenceButton, setEnabledSilenceButton] = useState(false)
    const [silenceDuration, setSilenceDuration] = useState(DEFAULT_SILENCE_DURATION)
    const [silenceComment, setSilenceComment] = useState("")
    const [isRecheck, setIsRecheck] = useState(false)
    const [isFailedRecheck, setIsFailedRecheck] = useState(false)
    const silenceDurationResult = useMemo(() => parseSilenceDuration(silenceDuration), [silenceDuration])

    const isRecheckableGroup = group.alerts.some(alert => RECHECKABLE_INPUT_PLUGIN.includes(alert.plugin_id))

    let alertBackground
    let fontColor
    let silenceRule
    let silenceRuleHost = ""
    let silenceRuleAlertName = ""
    let recheckRequest = []

    useEffect(() => {
        if (silenceComment && silenceComment.length > 4 && silenceDurationResult.isValid){
            setEnabledSilenceButton(true)
        } else {
            setEnabledSilenceButton(false)
        }
    }, [silenceComment, silenceDurationResult.isValid]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isEnabledSilenceWindow) {
                setEnabledSilenceWindow(false)
            }
        }
        window.addEventListener('keydown', handleEscape)

        return () => {
            window.removeEventListener('keydown', handleEscape)
        }

    }, [isEnabledSilenceWindow])

    const recheckStatus = useMemo(() =>{
        const alertIds = group.alerts.map(alert => alert.alert_id)
        const matchedRechecks = recheckedAlerts.filter(entry => alertIds.includes(entry.alert_id))
        if (matchedRechecks.length > 0){
            return matchedRechecks.every(alert => alert.recheck_status === "poo") ? "poo" : "other"
        }
        return null
    }, [recheckedAlerts, group.alerts])

    useEffect(() => {
        switch (recheckStatus) {
            case "poo":
                setIsRecheck(false)
                setIsFailedRecheck(true)
                break
            case "other":
                setIsRecheck(true)
                setIsFailedRecheck(false)
                break
            default:
                setIsRecheck(false)
                setIsFailedRecheck(false)
        }
    }, [recheckStatus])



    const onAlertClickHandler = (e) => {
        e.preventDefault()
        setIsAlertBlockOpened(!isAlertsBlockOpened)
    }

    //Check if all alerts handled by the same user
    const isOneGroupUser = group.alerts.every(alert => {
        return alert.responsibleUser === group.alerts[0].responsibleUser
    })

    //define icon for group responsible user
    const groupUserImage = (isOneGroupUser && group.alerts[0].responsibleUser !== "")
        ? `https://gravatar.com/avatar/${sha256(group.alerts[0].responsibleUser)}?s=150`
        : "/images/user.svg"

    const ackedAlerts = group.alerts.map(alert => {
            return {"alertId": alert.alert_id}
    })

    switch (group.severity.toUpperCase()){
        case 'UNKNOWN':
            alertBackground = '#ffda9f'
            fontColor = 'black'
            break
        case 'INFO':
            alertBackground = '#BED8FF'
            fontColor = 'black'
            break
        case 'WARNING':
            alertBackground = '#FEFFC1'
            fontColor = 'black'
            break
        case 'CRITICAL':
            alertBackground = '#FFBBBA'
            fontColor = 'black'
            break
        case 'EMERGENCY':
            alertBackground = '#f67d7a'
            fontColor = 'black'
            break
        default:
            alertBackground = '#858585'
            fontColor = 'black'
    }

    const onAckClickHandle =  async () => {
        try {
            // if all alerts are acked by current user, they will be unacked
            if (isOneGroupUser && group.alerts[0].responsibleUser === userEmail){
                await AlertService.unack(ackedAlerts)
            } else {
                // if only some of the alerts are acked by current user, they will not be changed
                // all other alerts will be acked by current user
                const unackedAlerts = []
                group.alerts.forEach(alert => {
                    if (alert.responsibleUser !== userEmail){
                        unackedAlerts.push({alertId: alert.alert_id})
                    }
                })
                await AlertService.ack(unackedAlerts)
            }
        }
        catch (e) {
            dispatch(setModalError("Oops. Something went wrong. Please, try a bit later"))
        }
    }

    const onRecheckClick = async () => {
        try {
            await AlertService.refreshAlerts([recheckRequest])
        }
        catch (e) {
            dispatch(openModal())
            dispatch(setModalError(e.response.data.name))
        }
    }


    //define group factor related settings
    if (group.groupFactor.startsWith('Host:')){
        silenceRuleHost = group.groupFactor.substring('Host: '.length);
        recheckRequest = ["recheck_host", group.alerts[0].host]
    }

    if (group.groupFactor.startsWith('Alert Name:')){
        silenceRuleAlertName = group.groupFactor.substring('Alert Name: '.length);
        recheckRequest = ["recheck_alert", group.alerts[0].alertName]
    }

    const handleSilenceButton = () => {
        setEnabledSilenceWindow(!isEnabledSilenceWindow)
    }

    const handleEnterKeyDown = (e) => {
        if (e.keyCode === 13) {
            e.preventDefault()
            if (isEnabledSilenceButton) {
                submitSilenceGroup()
            }
        }
    }

    const submitSilenceGroup = async () => {
        if (!silenceDurationResult.isValid) {
            dispatch(setModalError(silenceDurationResult.error))
            return
        }

            silenceRule = {
                project: "",
                host: silenceRuleHost,
                alertName: silenceRuleAlertName,
                startSilence: Date.now(),
                endSilence: parseSilenceDuration(silenceDuration).endSilence,
                comment: silenceComment

        }
        try {
            await AlertService.silence(silenceRule)
        }
        catch (e){
            dispatch(setModalError("Oops. Something went wrong. Please, try a bit later"))
        }

        setSilenceDuration(DEFAULT_SILENCE_DURATION)
        setSilenceComment("")
        setEnabledSilenceWindow(false)
    }


    return (
        <div className={styles.groupSection} key={group.id}>
            <div
                className={`${alertStyles.alertBody} ${styles.groupBody} ${!isInspectMode ? null : alertStyles.alertBody_small && styles.groupBodyInspected}`}
                style={{
                    backgroundColor: alertBackground,
                    color: fontColor,
                    height: `${alertHeight * 0.9}px`,
                    cursor: 'pointer'
                }}>
                <div className={`${!isInspectMode ? alertStyles.projectName : alertStyles.projectName_small}`}
                     onClick={e => onAlertClickHandler(e)}>{group.project[0].toUpperCase()}</div>
                <div
                    className={`${!isInspectMode ? alertStyles.host : alertStyles.host_small} ${!isInspectMode ? styles.alertsNumber : styles.alertsNumber_small}`}
                    onClick={e => onAlertClickHandler(e)}>
                    {group.alerts.length}
                </div>
                <div className={`${!isInspectMode ? alertStyles.severity : alertStyles.severity_small}`}
                     onClick={e => onAlertClickHandler(e)}>{group.severity}</div>
                <button
                    className={`${!isInspectMode ? alertStyles.responsibleUser : alertStyles.responsibleUser_small}`}
                    style={{backgroundImage: `url(${groupUserImage})`}}
                    onClick={e => {
                        e.preventDefault()
                        onAckClickHandle()
                    }}
                    disabled={displayMode === HISTORY_DISPLAY || displayMode === SILENCED_DISPLAY}
                />
                <div className={`${!isInspectMode ? alertStyles.alertName : alertStyles.alertName_small} ${styles.alertsName}`}
                     onClick={e => onAlertClickHandler(e)}>{group.groupFactor}</div>
                <div
                    className={`${!isInspectMode ? alertStyles.alertTime : alertStyles.alertTime_small}`}
                    onClick={e => onAlertClickHandler(e)}>
                </div>
                <div className={`${!isInspectMode ? styles.alertsGroupMessage : styles.alertsGroupMessage_small}`}
                     onClick={e => onAlertClickHandler(e)}>
                    <p className={alertStyles.textFields}> {group.description}</p>
                </div>
                <div
                    className={`${alertStyles.silenceWindow} ${isEnabledSilenceWindow ? alertStyles.silenceWindowActive : alertStyles.silenceWindowDisabled} ${!isInspectMode ? null : alertStyles.silenceWindow_small}`}>
                    <input type="text"
                           className={`${alertStyles.silenceDuration} ${!isInspectMode ? null : alertStyles.silenceDuration_small} ${silenceDurationResult.error ? alertStyles.silenceInputInvalid : ''}`}
                           placeholder="30m, 2h, 1 day"
                           id={`duration_${group.id}`}
                           value={silenceDuration}
                           title={silenceDurationResult.error || (silenceDuration.trim() ? `Until ${formatSilenceEnd(parseSilenceDuration(silenceDuration).endSilence)}` : 'Use values like 30m, 2h, 1 day, 1 month')}
                           onKeyDown={handleEnterKeyDown}
                           onChange={e => setSilenceDuration(e.target.value)}
                    />
                    <input type="text"
                            className={`${alertStyles.silenceComment} ${!isInspectMode ? null : alertStyles.silenceComment_small}`}
                            placeholder="comment"
                            id={`comment_${group.id}`}
                            value={silenceComment}
                            onKeyDown={handleEnterKeyDown}
                            onChange={e => setSilenceComment(e.target.value)}
                    />
                    <button disabled={!isEnabledSilenceButton}
                            className={`${isEnabledSilenceButton ? alertStyles.silenceButtonEnabled : alertStyles.silenceButtonDisabled} ${alertStyles.silenceButton} ${!isInspectMode ? null : alertStyles.silenceButton_small}`}
                            onClick={(e) => {
                                e.preventDefault()
                                submitSilenceGroup()
                            }}
                    >
                        Silence
                    </button>
                </div>
                <div className={`${alertStyles.functionButtonsBlock} ${isInspectMode ? alertStyles.functionButtonsBlock_small : null}`}>
                    <div className={`${!isInspectMode ? alertStyles.controlButton : alertStyles.controlButton_small}`}/>
                    {/*group recheck button may be hidden if alerts are not recheckable*/}
                    <button
                        className={`${!isInspectMode ? alertStyles.controlButton : alertStyles.controlButton_small} 
                ${isRecheckableGroup
                            ? `${isFailedRecheck ? alertStyles.failedRefresh : alertStyles.refresh} ${isRecheck ? commonStyles.rotatedIcon : commonStyles.buttonHint}`
                            : ''} `}
                        data-tooltip="recheck group"
                        onClick={(e) => {
                            e.preventDefault()
                            onRecheckClick()
                        }}
                        disabled={isRecheck || !isRecheckableGroup}
                    />
                    {displayMode === MAIN_DISPLAY
                        ? <div
                            className={`${!isInspectMode ? alertStyles.controlButton : alertStyles.controlButton_small} ${alertStyles.silence} ${commonStyles.buttonHint}`}
                            data-tooltip="silence group"
                            onClick={(e) => {
                                e.preventDefault()
                                handleSilenceButton()
                            }}
                        />
                        : null
                    }
                </div>


            </div>
            <div className={`${styles.alertBlock} ${isAlertsBlockOpened ? styles.alertBlockHidden : ''}`}
                 style={{height: `${alertHeight * group.alerts.length}px`, transition: "height 0.3s"}}>
                {
                    group.alerts.map(alert => (
                        <div style={{height: `${alertHeight}px`}}>
                            <Alert alert={alert} isGroupRecheck={isRecheck}/>
                        </div>
                    ))
                }
            </div>
        </div>
    );
};

export default AlertGroup;
